import type { TFunction } from "i18next";
import { DateTime } from "luxon";

type SummaryValue = string | number;

type SummaryLine = {
	key: string;
	params: Record<string, SummaryValue>;
};

type SummaryPayload = {
	version: number;
	loadType: string;
	lines: SummaryLine[];
};

const UNASSIGNED_VALUE = "__unassigned__";
const UNKNOWN_JOB_VALUE = "__unknown_job__";

const isSummaryPayload = (value: unknown): value is SummaryPayload => {
	if (!value || typeof value !== "object" || !("lines" in value)) return false;
	// JSON.parse returns unknown; this narrow cast is confined to the guard that validates the field.
	const payload = value as { lines: unknown };
	return (
		Array.isArray(payload.lines) &&
		payload.lines.every(
			(line) =>
				line &&
				typeof line === "object" &&
				"key" in line &&
				typeof line.key === "string" &&
				"params" in line &&
				typeof line.params === "object" &&
				line.params !== null,
		)
	);
};

const formatDate = (value: string, timezone?: string): string => {
	const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
	const parsed = isDateOnly ? DateTime.fromISO(value, timezone ? { zone: timezone } : {}) : DateTime.fromISO(value, { setZone: true });
	if (!parsed.isValid) return value;
	const zoned = isDateOnly || !timezone ? parsed : parsed.setZone(timezone);
	return zoned.toLocaleString(DateTime.DATE_MED);
};

const formatDateTime = (value: string, timezone?: string): string => {
	const parsed = DateTime.fromISO(value, { setZone: true });
	if (!parsed.isValid) return value;
	const zoned = timezone ? parsed.setZone(timezone) : parsed;
	return zoned.toLocaleString(DateTime.DATETIME_MED);
};

const formatTime = (value: string, timezone?: string): string => {
	const isTimeOnly = /^\d{2}:\d{2}(:\d{2}(?:\.\d+)?)?$/.test(value);
	const parsed = DateTime.fromISO(value, isTimeOnly ? {} : { setZone: true });
	if (!parsed.isValid) return value;
	const zoned = isTimeOnly || !timezone ? parsed : parsed.setZone(timezone);
	return zoned.toLocaleString(DateTime.TIME_SIMPLE);
};

const localizeValue = (value: SummaryValue, t: TFunction): SummaryValue => {
	if (value === UNASSIGNED_VALUE) return t("data_load_summary.unassigned");
	if (value === UNKNOWN_JOB_VALUE) return t("data_load_summary.unknown_job");
	return value;
};

const localizeLine = (line: SummaryLine, t: TFunction, timezone?: string): string => {
	const params: Record<string, SummaryValue> = Object.fromEntries(Object.entries(line.params).map(([key, value]) => [key, localizeValue(value, t)]));
	if (typeof params.loaded_at === "string") params.loaded_at = formatDateTime(params.loaded_at, timezone);
	if (typeof params.first_date === "string") params.first_date = formatDate(params.first_date, timezone);
	if (typeof params.last_date === "string") params.last_date = formatDate(params.last_date, timezone);
	if (typeof params.analyzed_last_date === "string") params.analyzed_last_date = formatDate(params.analyzed_last_date, timezone);
	if (line.key === "data_load_summary.shifts.coverage_flag" && typeof params.date === "string") params.date = formatDate(params.date, timezone);
	if (line.key === "data_load_summary.diagnostics.overlap_item") {
		for (const key of ["shift1_start", "shift1_end", "shift2_start", "shift2_end"]) {
			if (typeof params[key] === "string") params[key] = formatDateTime(params[key], timezone);
		}
	}
	if (line.key === "data_load_summary.diagnostics.skipped_pto_item") {
		if (typeof params.start_date === "string") params.start_date = formatDate(params.start_date, timezone);
		if (typeof params.end_date === "string") params.end_date = formatDate(params.end_date, timezone);
		if (typeof params.blocked_origin === "string") params.blocked_origin = t(`data_load_summary.diagnostics.origin.${params.blocked_origin}`);
		if (typeof params.blocked_type === "string" && params.blocked_type) params.blocked_type = t(`data_load_summary.diagnostics.type.${params.blocked_type}`);
	}
	if (line.key === "data_load_summary.diagnostics.unrecognized_window") {
		if (typeof params.date === "string") params.date = formatDate(params.date, timezone);
		if (typeof params.start === "string") params.start = formatTime(params.start, timezone);
		if (typeof params.end === "string") params.end = formatTime(params.end, timezone);
	}
	if (line.key === "data_load_summary.shifts.time_breakdown_item") {
		if (typeof params.start === "string") params.start = formatTime(params.start, timezone);
		if (typeof params.end === "string") params.end = formatTime(params.end, timezone);
	}
	return t(line.key, params);
};

export const localizeValidationSummary = (summaryText: string, t: TFunction, timezone?: string): string => {
	try {
		const parsed: unknown = JSON.parse(summaryText);
		if (isSummaryPayload(parsed)) return parsed.lines.map((line) => localizeLine(line, t, timezone)).join("\n");
	} catch {
		// DEFAULT: preserve summaries written before the structured, locale-neutral format existed.
	}
	return summaryText;
};
